import { GlobalStateController } from "bai-react-global-state";
import _ from "lodash";

import getLogger, { LogGroup } from "js/core/logger";
import { findElementsWithImages } from "../../../../common/utils/findElementsWithImages";
import getObjectHash from "../../../../common/utils/getObjectHash";
import resolveImageSource from "../../../../common/utils/resolveImageSource";
import AiGenerationService from "../../../core/services/aiGeneration";
import { getTextFromBlocks } from "../../../core/utilities/htmlTextHelpers";
import { ShowWarningDialog } from "../../../react/components/Dialogs/BaseDialog";
import WorkspaceController from "../../../controllers/WorkspaceController";

const logger = getLogger(LogGroup.AI);

const initialState = {
    currentSlide: null,
    generatedIdeas: [],
    isGenerating: false,
    slideIdeas: [],
    slideCategories: [],
    slideImage: null,
    files: []
};

export class SlideAiController extends GlobalStateController {
    get currentSlide() {
        return this._state.currentSlide;
    }

    getCategoryController() {
        return import(/* webpackMode: "eager" */ "common/SlideCategories/CategoryController").then(module => module.CategoryController);
    }

    async setCurrentSlide(slide, canvasController) {
        if (this._state.currentSlide) {
            this._state.currentSlide.off("change:metadata", this.onMetadataUpdated);
        }

        if (!slide) {
            await this.reset();
            return;
        }

        let metadata = slide.getMetadata();

        this.generatingImagesFor = null;

        await this._updateState({
            ...initialState,
            currentSlide: slide,
            canvasController,
            generatedIdeas: metadata.generatedIdeas || [],
            slideImage: metadata.generatedModel?.slideImage || null,
            slideCategories: [],
            isGenerating: false,
            generateError: metadata.generateError,
        });

        await this.resetFiles();

        slide.on("change:metadata", this.onMetadataUpdated);

        this.buildSlideModels();
    }

    async reset() {
        await this._updateState(() => ({
            ...initialState,
        }));
    }

    async resetFiles() {
        await this._updateState({
            files: this.getFilesFromSlideMetadata()
        });
    }

    getFilesFromSlideMetadata() {
        let { currentSlide } = this._state;
        let metadata = currentSlide.getMetadata();
        return metadata.files ? metadata.files.map(f => f.hasBeenUsedFor === "slide" ? ({ ...f, hasBeenUsed: false }) : f) : [];
    }

    setIsGenerating(isGenerating) {
        this._updateState({ isGenerating });
    }

    onMetadataUpdated = async (slide, changes) => {
        if (this._state.currentSlide !== slide) {
            return;
        }

        if (changes.generatedIdeas) {
            const metadata = slide.getMetadata();

            this._updateState({
                generatedIdeas: metadata.generatedIdeas || [],
                slideImage: metadata.generatedModel?.slideImage || null
            });

            this.buildSlideModels();
        } else if (changes.generatedModel?.slideImage) {
            this.buildSlideModels();
        }
    }

    buildPromptFromSlide(canvas) {
        let prompt = "";

        try {
            let header = canvas.getHeaderElement();
            if (header) {
                prompt += header.buildSlidePrompt() + "\n\n";
            }

            let primary = canvas.getPrimaryElement();
            if (primary) {
                prompt += primary.buildSlidePrompt() + "\n";
            }

            let sideBar = canvas.getSideBarElement();
            if (sideBar) {
                prompt += sideBar.buildSlidePrompt() + "\n";
            }

            let bottomTray = canvas.getTextTrayElement();
            if (bottomTray) {
                const trayText = getTextFromBlocks(bottomTray.model?.elementTextBlock).trim();
                if (trayText) {
                    prompt += "## Conclusion\n\n" + trayText + "\n";
                }
            }

            if (canvas.slide.getMetadata()?.generatedModel?.slideImage?.prompt) {
                prompt += "\nVisual: " + canvas.slide.getMetadata().generatedModel.slideImage.prompt + "\n";
            }
        } catch (err) {
            logger.error(err, "[SlideAiController] Error building prompt from slide");
        }

        return prompt;
    }

    async _waitForFilesToProcess() {
        let getFiles = () => this._state.files.filter(f => !f.hasBeenUsed);

        let processingFiles = getFiles().filter(f => f.isProcessing);
        while (processingFiles.length > 0) {
            await new Promise(resolve => setTimeout(resolve, 100));
            processingFiles = getFiles().filter(f => f.isProcessing);
        }

        return getFiles();
    }

    async regenerateIdeas(slidePrompt, {
        slideType,
        language,
        preserveExactText = false,
        usePresentationAsContext = true,
        webSearch = false,
        imageSource = "ai",
        regenerateSlideImage = true,
        imageStyle = null,
        imageModel = null
    } = {}) {
        const { currentSlide } = this._state;

        this._updateState({ isGenerating: true });

        const files = await this._waitForFilesToProcess();
        files.forEach(file => {
            file.hasBeenUsed = true;
            file.hasBeenUsedFor = "slide";
        });

        const genProps = {
            slidePrompt,
            slideType,
            language,
            preserveExactText,
            usePresentationAsContext,
            files,
            webSearch
        };

        imageSource = resolveImageSource(imageSource, WorkspaceController.actionPermissions);

        if (!regenerateSlideImage && imageSource !== "none") {
            if (
                // No slide image defined, have to generate anyway
                !currentSlide.getMetadata().generatedModel?.slideImage ||
                // Image source has changed, have to regenerate
                currentSlide.getMetadata().imageSource !== imageSource

            ) {
                regenerateSlideImage = true;
            }
        }

        let response, slideImageUpdates;
        await Promise.all([
            (async () => {
                if (!regenerateSlideImage) {
                    slideImageUpdates = { metadata: {} };
                    return;
                }

                let { slideImagePrompt } = await AiGenerationService.generateSlideImagePrompt(
                    slidePrompt,
                    usePresentationAsContext ? currentSlide.presentation.get("metadata")?.topic : null
                );

                imageStyle = imageStyle || currentSlide.getMetadata().generatedModel?.slideImage?.imageStyle || "";

                let { slide } = await AiGenerationService.regenerateSlideImage({
                    slide: currentSlide.attributes,
                    imagePrompt: slideImagePrompt,
                    imageStyle,
                    imageSource,
                    imageModel,
                    replaceImages: false
                });

                slideImageUpdates = slide;
            })(),
            (async () => {
                try {
                    response = await AiGenerationService.generateSlideData({
                        slideId: currentSlide.id,
                        presentationId: currentSlide.presentation.id,
                        slideIndex: currentSlide.getIndex(),
                        presentationTopic: currentSlide.presentation.get("metadata")?.topic || "",
                        ...genProps
                    });
                } catch (err) {
                    logger.error(err, "[SlideAiController] Error generating slide data");
                }
            })(),
        ]);

        if (!response || !response.slides || response.slides.length == 0) {
            ShowWarningDialog({ title: "Slide AI", message: "Sorry, an error occurred while generating slide ideas. This may be a temporary issue so please try again." });

            this._updateState({ isGenerating: false });
            return;
        }

        if (this._state.currentSlide !== currentSlide) {
            return;
        }

        let generatedIdeas = response.slides.map(slide => slide.slideData);

        let savedFiles = [];
        if (files.length > 0) {
            ({ files: savedFiles } = await AiGenerationService.saveContextDocuments(files));
        }

        currentSlide.update({
            ...slideImageUpdates,
            hasBeenViewed: true,
            metadata: {
                ...currentSlide.getMetadata(),
                ...slideImageUpdates.metadata,
                ...genProps,
                imageSource,
                generatedIdeas,
                files: savedFiles,
                presentationAsContext: usePresentationAsContext,
                gotUserFeedback: false,
            }
        }, { silent: true, saveUndo: true });

        await this._updateState({
            generatedIdeas,
            slideImage: currentSlide.getMetadata().generatedModel?.slideImage,
        });

        this.generatingImagesFor = null;

        await this.buildSlideModels();

        this._updateState({ isGenerating: false });
    }

    async buildSlideModels() {
        const { currentSlide, generatedIdeas, slideImage } = this._state;

        if (!currentSlide) {
            await this._updateState({ slideCategories: [] });
            return;
        }

        let imageSource = resolveImageSource(currentSlide.getMetadata()?.imageSource ?? "ai", WorkspaceController.actionPermissions);

        let slideCategories = [];
        for (let idea of generatedIdeas) {
            let slideModel = idea.slideModel;

            slideModel.slideImage = slideImage;

            // build slide models for this category
            let CategoryController = await this.getCategoryController();
            let category = CategoryController.getCategory(idea.slideCategory);
            if (category.validateGeneratedModel(slideModel) === false) {
                logger.warn(`[SlideAiController] Generated model for category ${idea.slideCategory} failed validation`);
                continue;
            }
            let models = category.getSlideModels(slideModel).filter(model => model != null);

            // copy over metadata from the current slide
            models = models.map(model => ({
                ...model,
                metadata: {
                    ...currentSlide.getMetadata(),
                    ...model.metadata,
                    generatedIdeas,
                    aiGenerated: true,
                    slideCategory: idea.slideCategory,
                    slideReasoning: idea.slideReasoning,
                    slideConfidence: idea.slideConfidence,
                    needsLayoutProcessing: true
                }
            }));

            models.forEach(model => {
                let elementsWithImages = findElementsWithImages(model);
                for (let element of elementsWithImages) {
                    if (element.__generateImage) {
                        element.__generateImage.imageStyle = slideImage?.imageStyle;
                        element.__generateImage.imageSource = slideImage?.imageSource;
                        element.__generateImage.imageModel = slideImage?.imageModel;

                        if (imageSource === "none") {
                            element.content_value = null;
                            element.__generateImage.isGenerating = false;
                        } else if (!element.content_value) {
                            element.__generateImage.isGenerating = true;
                        }
                    }
                }
            });

            slideCategories.push({
                slideCategory: category.name,
                slideReasoning: idea.slideReasoning,
                slideConfidence: idea.slideConfidence,
                models
            });
        }

        if (imageSource !== "none") {
            let ideasHash = getObjectHash(generatedIdeas);
            if (this.generatingImagesFor === ideasHash) {
                return;
            }
            this.generatingImagesFor = ideasHash;

            slideCategories.forEach(category => {
                category.models.forEach((model, idx) => {
                    let elementsWithImages = findElementsWithImages(model);
                    if (elementsWithImages.some(element => element.__generateImage.isGenerating && !element.__generateImage.isSlideImage)) {
                        AiGenerationService.generateImagesForSlide({ slide: { ...model, presentationId: currentSlide.get("presentationId") } })
                            .then(({ slide }) => {
                                if (this._state.currentSlide !== currentSlide) {
                                    return;
                                }

                                category.models[idx] = _.omit(slide, "presentationId");
                                this._updateState({ slideCategories });
                            });
                    }
                });
            });
        }

        await this._updateState({ slideCategories });
    }

    async parseDocument(file) {
        while (this._state.files.some(f => f.name === file.name)) {
            file = new File([file], `_${file.name}`, { type: file.type });
        }

        await this._updateState(state => ({
            ...state,
            files: [...state.files, { name: file.name, type: file.type, isProcessing: true }]
        }));

        try {
            let parsedFile = await AiGenerationService.parseDocument(file, this._state.files);

            await this._updateState(state => ({
                ...state,
                files: [...state.files.filter(f => f.name !== parsedFile.name), parsedFile]
            }));
        } catch (err) {
            await this._updateState(state => ({
                ...state,
                files: state.files.filter(f => f.name !== file.name)
            }));

            throw err;
        }
    }

    async removeFile(name) {
        await this._updateState(state => ({
            ...state,
            files: state.files.filter(f => f.name !== name)
        }));
    }
}

const slideAiController = new SlideAiController(initialState);
export default slideAiController;

// DEV-START
if (import.meta.webpackHot) {
    const reloadIdeas = async () => {
        if (window.isReloadingIdeas) {
            // eslint-disable-next-line no-console
            console.log("[HMR] Already reloading ideas");
            return;
        }

        if (!slideAiController.currentSlide) {
            return;
        }

        // eslint-disable-next-line no-console
        console.log("[HMR] Reloading ideas");

        window.isReloadingIdeas = true;
        try {
            await slideAiController.buildSlideModels();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[HMR] Error reloading ideas", err);
        } finally {
            window.isReloadingIdeas = false;
        }
    };

    let isReady = false;
    const onWebpackReload = status => {
        if (status === "ready") {
            isReady = true;
            return;
        }

        if (status === "idle" && isReady) {
            return reloadIdeas();
        }
    };

    import.meta.webpackHot.addStatusHandler(onWebpackReload);

    import.meta.webpackHot.accept(["common/SlideCategories/CategoryController"], () => {
        return reloadIdeas();
    });
}
// DEV-END
